import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { styled } from '../../../../../shared/Theme/theme';
import { DropDown } from '../../../../../shared/inputs/base';
import Button from '../../../../../shared/SmallComponents/Button';
import Loader from '../../../../../shared/SmallComponents/Loader';
import { businessPartner } from '../../../../../../models/entities/businessPartner/businessPartner';
import { bpAccountingResponse, bpAccountingRow } from '../../../../../../models/entities/businessPartner/accounting';
import { contact } from '../../../../../../models/entities/businessPartner/contact';
import { settings as shipmentSettings } from '../../../../../../models/entities/shipment/shipment';
import { localSDK as client } from '../../../../../../sdk';
import {
    BpInvoiceIssuer,
    paymentTermsDays,
    paymentTermsLabelFromCode
} from './invoicePdfTypes';
import { getBpInvoicePdfFile, getBpStatementPdfFile } from './invoicePdfUtils';
import {
    bpInvoiceSummary,
    computeInvoiceAmount,
    fetchShipmentInvoiceContext,
    resolveBillingLinesForInvoice
} from '../../../../commonSubEntities/accounting/invoiceLineTracking';

export type SendInvoiceModalProps = {
    businessPartner: businessPartner;
    row: bpAccountingRow;
    accountingData: bpAccountingResponse;
    issuer: BpInvoiceIssuer;
    shipmentSettings?: shipmentSettings;
    localization: {
        send_modal: {
            contacts_placeholder: string;
            send_btn: string;
            includes_documents: string;
            no_financial_contacts: string;
            success: string;
            error: string;
        };
    };
    onClose: () => void;
    onSuccess?: () => void;
    /** Refreshes invoice details column on shipment accounting tab. */
    onInvoiceSent?: () => void;
};

const NEW_INVOICE_VALUE = '__new__';

const SendInvoiceModal = ({
    businessPartner,
    row,
    accountingData,
    issuer,
    shipmentSettings,
    localization,
    onClose,
    onSuccess,
    onInvoiceSent
}: SendInvoiceModalProps) => {
    const [emails, setEmails] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [invoices, setInvoices] = useState<bpInvoiceSummary[]>([]);
    const [uninvoicedCount, setUninvoicedCount] = useState(0);
    const [selectedInvoiceKey, setSelectedInvoiceKey] = useState<string>(NEW_INVOICE_VALUE);

    const loadInvoiceContext = useCallback(async () => {
        setLoadingInvoices(true);
        setError(null);
        try {
            const ctx = await fetchShipmentInvoiceContext(row.shipmentId, businessPartner.id);
            setInvoices(ctx.invoices);
            setUninvoicedCount(ctx.uninvoicedCount);
            if (ctx.uninvoicedCount > 0) {
                setSelectedInvoiceKey(NEW_INVOICE_VALUE);
            } else if (ctx.invoices[0]) {
                setSelectedInvoiceKey(ctx.invoices[0].id);
            }
        } catch (e) {
            setInvoices([]);
            setUninvoicedCount(0);
            setError((e as Error)?.message || 'Could not load invoice history for this shipment.');
        } finally {
            setLoadingInvoices(false);
        }
    }, [row.shipmentId, businessPartner.id]);

    useEffect(() => {
        void loadInvoiceContext();
    }, [loadInvoiceContext]);

    const contactOptions = useMemo(() => {
        const withEmail = businessPartner.contacts?.filter((c: contact) => c.email) ?? [];
        const financial = withEmail.filter((c) => c.receiveFinancialDocuments);
        const manualOnly = withEmail.filter((c) => !c.receiveFinancialDocuments);
        const opts = [
            ...financial.map((c) => ({ value: c.email, text: `${c.name} (${c.email})` })),
            ...manualOnly.map((c) => ({
                value: c.email,
                text: `${c.name} (${c.email}) — manual only`
            }))
        ];
        const seen = new Set<string>();
        return opts.filter((o) => {
            const k = o.value.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }, [businessPartner.contacts]);

    const defaultEmails = useMemo(() => {
        const fromContacts =
            businessPartner.contacts
                ?.filter((c) => c.receiveFinancialDocuments && c.email)
                .map((c) => c.email) ?? [];
        if (fromContacts.length) return fromContacts;
        return businessPartner.email ? [businessPartner.email] : [];
    }, [businessPartner]);

    useEffect(() => {
        setEmails(defaultEmails);
        setMessage(null);
    }, [defaultEmails, row.shipmentId]);

    const canSend = uninvoicedCount > 0 || invoices.length > 0;

    const invoiceOptions = useMemo(() => {
        const opts: Array<{ value: string; text: string }> = [];
        if (uninvoicedCount > 0) {
            opts.push({
                value: NEW_INVOICE_VALUE,
                text: `New invoice (${uninvoicedCount} uninvoiced line${uninvoicedCount === 1 ? '' : 's'})`
            });
        }
        invoices.forEach((inv) => {
            const sent = inv.sentAt ? ` · sent ${moment(inv.sentAt).format('DD/MM/YY')}` : '';
            const dated = moment(inv.invoiceDate).format('DD/MM/YYYY');
            opts.push({
                value: inv.id,
                text: `${inv.invoiceNumber} · ${dated}${sent} (${inv.billingLineIds.length} lines)`
            });
        });
        return opts;
    }, [invoices, uninvoicedCount]);

    const serviceTypeLabel = (code: string) =>
        shipmentSettings?.billingLineServiceType?.find((s) => s.code === code)?.name || code;

    const handleSend = async () => {
        if (!emails.length) {
            setError('Select at least one recipient.');
            return;
        }
        if (!canSend) {
            setError('No invoice available to send.');
            return;
        }
        if (!invoiceOptions.some((o) => o.value === selectedInvoiceKey)) {
            setError('Select an invoice to send.');
            return;
        }
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            const invoiceId = selectedInvoiceKey === NEW_INVOICE_VALUE ? null : selectedInvoiceKey;
            const resolved = await resolveBillingLinesForInvoice({
                shipmentId: row.shipmentId,
                businessPartnerId: businessPartner.id,
                invoiceId
            });
            const shipment = await client.shipments().fetchById(row.shipmentId);
            const termsCode = businessPartner.paymentTerms;
            const days = paymentTermsDays(termsCode);
            const dueDate =
                days != null && row.shipmentDate
                    ? new Date(new Date(row.shipmentDate).getTime() + days * 86400000)
                    : row.dueDate
                      ? new Date(row.dueDate)
                      : null;
            const currency = row.currency || accountingData.kpis.currency;
            const invoiceAmount = computeInvoiceAmount(resolved.billingLines);
            const accountNumber =
                businessPartner.BusinessPartnerNumber ||
                (businessPartner.bpNumber != null ? String(businessPartner.bpNumber) : businessPartner.id.slice(0, 8));

            const invoiceFile = await getBpInvoicePdfFile({
                businessPartner,
                shipment,
                billingLines: resolved.billingLines,
                issuer,
                invoiceNumber: resolved.invoiceNumber,
                invoiceDate: resolved.isNew ? new Date() : new Date(invoices.find((i) => i.id === resolved.invoiceId)?.invoiceDate ?? Date.now()),
                dueDate,
                paymentTermsLabel: paymentTermsLabelFromCode(termsCode) || accountingData.kpis.paymentTermsLabel,
                currency,
                serviceTypeLabel
            });

            const statementFile = await getBpStatementPdfFile({
                businessPartner,
                accounting: accountingData,
                issuer,
                statementDate: new Date(),
                accountNumber: `AC-${accountNumber}`
            });

            const result = await client.businessPartners().accounting().sendInvoicePackage(businessPartner.id, {
                shipmentId: row.shipmentId,
                shipmentNumber: row.shipmentNumber,
                contactEmails: emails,
                invoiceFile,
                statementFile,
                invoiceAmount,
                invoiceCurrency: currency,
                invoiceId: resolved.invoiceId
            });

            await loadInvoiceContext();
            onInvoiceSent?.();

            const docNote =
                result.attachedDocumentCount > 0
                    ? ` (${result.attachedDocumentCount} document${result.attachedDocumentCount === 1 ? '' : 's'} from Documents tab)`
                    : '';
            setMessage(`${localization.send_modal.success}${docNote}`);
            onSuccess?.();
        } catch (e) {
            setError((e as Error)?.message || localization.send_modal.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Body>
            <Hint>{localization.send_modal.includes_documents}</Hint>
            {!defaultEmails.length && <Warn>{localization.send_modal.no_financial_contacts}</Warn>}

            <FieldLabel>Invoice to send</FieldLabel>
            {loadingInvoices ? (
                <Loader width="20px" wrapperWidth="auto" marginTop="0" showText={false} inline />
            ) : !canSend ? (
                <EmptyState>No billing lines available to invoice for this shipment.</EmptyState>
            ) : (
                <DropDown
                    options={invoiceOptions}
                    value={selectedInvoiceKey}
                    onChange={(v: string) => setSelectedInvoiceKey(v)}
                    width="100%"
                />
            )}
            <FieldHint>
                New invoices include only billing lines not yet invoiced. Resend reuses the same line snapshot — no duplicate ledger entry.
            </FieldHint>

            <FieldLabel>Recipients</FieldLabel>
            <DropDown
                options={contactOptions}
                value={emails}
                onChange={(v: string[]) => setEmails(v)}
                placeHolder={localization.send_modal.contacts_placeholder}
                mode="multiple"
                width="100%"
                showSearch
            />
            {error && <ErrorText>{error}</ErrorText>}
            {message && <SuccessText>{message}</SuccessText>}
            <Footer>
                <Button buttonType="hollow" height="34px" minWidth="72px" onClick={onClose} disabled={loading}>
                    <span>Cancel</span>
                </Button>
                <Button
                    buttonType="filled"
                    height="34px"
                    minWidth="100px"
                    onClick={() => void handleSend()}
                    disabled={loading || loadingInvoices || !canSend}
                >
                    {loading ? (
                        <Loader width="20px" wrapperWidth="auto" marginTop="0" showText={false} inline />
                    ) : (
                        <span>{localization.send_modal.send_btn}</span>
                    )}
                </Button>
            </Footer>
        </Body>
    );
};

const Body = styled.div`
    padding: 8px 4px 0;
`;

const Hint = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 12px;
    line-height: 1.45;
`;

const FieldHint = styled.p`
    font-size: 11px;
    color: #9ca3af;
    margin: 4px 0 14px;
    line-height: 1.4;
`;

const Warn = styled.p`
    font-size: 12px;
    color: #b45309;
    background: #fffbeb;
    padding: 8px 10px;
    border-radius: 6px;
    margin: 0 0 12px;
`;

const FieldLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
    color: #374151;
`;

const EmptyState = styled.div`
    font-size: 12px;
    color: #6b7280;
    padding: 10px 0;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    flex-shrink: 0;
`;

const ErrorText = styled.div`
    color: #b91c1c;
    font-size: 12px;
    margin-top: 10px;
`;

const SuccessText = styled.div`
    color: #15803d;
    font-size: 12px;
    margin-top: 10px;
`;

export default SendInvoiceModal;
